<script setup lang="ts">
import { IdvDocuments } from '@Constants/idvDocumentTypeDisplay.constants';
import type { IdvDocumentDisplay, IdvInstructionsCarouselSlideData, IdvCarouselInstance } from '@Types/MatchIdv.interface';
import { type PropType } from 'vue';
import InstructionsCarouselSlide from './InstructionsCarouselSlide.vue';

import IdFront from '@Assets/images/idv/be_prepared_DL_Other.svg';
import DlBarcode from '@Assets/images/idv/scan_barcode_DL.svg';
import OtherBarcode from '@Assets/images/idv/capture_back_Other.svg';
import PassportFront from '@Assets/images/idv/be_prepared_Passport.svg';

// CONSTANTS
const slideMap: Record<string, IdvInstructionsCarouselSlideData[]> = {
    [IdvDocuments.NorthAmericanDriversLicense.value]: [
        {
            image: IdFront,
            imageAlt: 'ID card on a dark background',
            title: 'Be prepared',
            description: 'Find a dark, flat surface to lay your ID on for an easy automatic capture.'
        },
        {
            image: DlBarcode,
            imageAlt: 'Scanning the barcode on the back of the ID',
            title: 'Scan the barcode',
            description: 'After capturing the front, flip your ID over and focus your camera on the barcode.'
        }
    ],
    [IdvDocuments.Passport.value]: [
        {
            image: PassportFront,
            imageAlt: 'Passport on a dark background',
            title: 'Be prepared',
            description: 'Find a dark surface to lay your passport on for an easy automatic capture.'
        },
    ],
    [IdvDocuments.UsPassportCard.value]: [
        {
            image: IdFront,
            imageAlt: 'ID card on a dark background',
            title: 'Be prepared',
            description: 'Find a dark, flat surface to lay your ID on for an easy automatic capture.'
        },
        {
            image: OtherBarcode,
            imageAlt: 'Back of an ID card on a dark background',
            title: 'Capture the back',
            description: 'After scanning the front, flip your ID over to capture the backside.'
        }
    ],
    [IdvDocuments.Other.value]: [
        {
            image: IdFront,
            imageAlt: 'ID card on a dark background',
            title: 'Be prepared',
            description: 'Find a dark, flat surface to lay your ID on for an easy automatic capture.'
        },
        {
            image: OtherBarcode,
            imageAlt: 'Back of an ID card on a dark background',
            title: 'Capture the back',
            description: 'After scanning the front, flip your ID over to capture the backside.'
        }
    ]
};

const carouselRefs: Record<string, IdvCarouselInstance> = {};

// PROPS
const props = defineProps({
    docType: Object as PropType<IdvDocumentDisplay>
});

// EMITS
const emit = defineEmits(['slide-changed']);

// METHODS
const isOnLastSlide = function() {
    let carousel = carouselRefs[`carousel_${props.docType?.value}`];
    return carousel?.isOnLastSlide();
};

const next = function() {
    let carousel = carouselRefs[`carousel_${props.docType?.value}`];
    carousel?.next();
};

const reset = function() {
    let carousel = carouselRefs[`carousel_${props.docType?.value}`];
    carousel?.goTo(0);
};

// EXPOSE
defineExpose({ next, isOnLastSlide, reset });

</script>

<template>
    <div>
        <template v-for="(slideGroup, groupName) in slideMap">
            <app-carousel
                v-if="slideGroup.length > 0"
                :key="groupName"
                :ref="(el: unknown) => {if (el) carouselRefs[`carousel_${groupName}`] = el as IdvCarouselInstance}"
                class="instructions-carousel"
                :class="{ 'instructions-hidden': docType?.value !== groupName, 'instructions-active' : docType?.value === groupName }"
                @slide-number-changed="emit('slide-changed')">
                <InstructionsCarouselSlide
                    v-for="(slide, index) in slideGroup"
                    :key="`${groupName}-${index}`"
                    :image="slide.image"
                    :slide-number="index + 1"
                    :total-slides="slideGroup.length"
                    :show-slide-number="slideGroup.length > 1"
                    :title="slide.title"
                    :image-alt="slide.imageAlt">
                    {{ slide.description }}
                </InstructionsCarouselSlide>
            </app-carousel>
        </template>
    </div>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.instructions-carousel {
    position: absolute;
    width: 100%;
    left: 0;

    &.instructions-active {
        position: relative;
        animation: fadeIn 0.8s ease forwards;
    }

    &.instructions-hidden {
        top: 16px;
        position: absolute;
        animation: fadeOut 0.8s ease forwards;
    }
}

@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

@keyframes fadeOut {
  0% { opacity: 1; }
  100% { opacity: 0; visibility: hidden; }
}

</style>